/**
 * תכנון ופיתוח מוצרים — the project card and everything hanging off it.
 *
 * Money is `number | null` in every DTO and a `DECIMAL` column in the database, converted on the
 * way out by `toMoney` from `productHub.money.ts` — the same rule the pricing module follows, and
 * for the same reason: a price the screen shows and a price the API adds have to be one number.
 *
 * Dates are ISO strings, because these cross the wire. `dueDate` is a date without a time
 * (`YYYY-MM-DD`); everything else is a full timestamp.
 *
 * No React, no MUI, no browser globals.
 */

import { PhCurrency, phOptions } from './productHub.core';
import { ProductionCustomerType } from './productionCustomer.types';
import { PlanningSpec } from './productPlanning.itemSpec';
import {
  PlanningFileCategory,
  PlanningProjectOrigin,
  PlanningSubmissionChannel,
  PlanningProjectStatus,
  PlanningProjectTypeCode,
  PlanningQuoteStatus,
  PlanningSampleDecision,
  PlanningStage,
  PlanningSupplierRef,
  PlanningUserRef
} from './productPlanning.core';

/* ── the project ──────────────────────────────────────────────────────────────────────────── */

/** Who the project is waiting on right now. Exactly one side is set, or neither. */
export interface PlanningWaitingOn {
  user: PlanningUserRef | null;
  supplier: PlanningSupplierRef | null;
}

/** The catalogue product a project is linked to, when it has one. Read-only — we never write it. */
export interface PlanningLinkedProduct {
  id: number;
  name: string;
  sku: string | null;
}

/**
 * The earlier development project an `upgrade` / `production_round` was opened from.
 *
 * Carries the `code` (`DEV-00042`) as well as the name and the SKU, because that is how the team
 * says which round it means out loud — and how the picker's list stays readable when three rounds
 * of the same kit share a name.
 */
export interface PlanningProjectSourceRef {
  id: number;
  code: string;
  name: string;
  sku: string | null;
}

/** תגית פרויקט. Many per project; nothing branches on one, it only filters. */
export interface PlanningProjectTagDto {
  id: number;
  name: string;
  /** The production customer the tag belongs to. Null when the tag is internal. */
  productionCustomerId: number | null;
}

/** The row shape of the main table. Deliberately flat: the mockup renders ten columns per row. */
export interface PlanningProjectRow {
  id: number;
  code: string;
  /** The project's own, always. A new round is a *copy* of its source and is renamed on its own. */
  name: string;
  sku: string | null;
  stage: PlanningStage;
  status: PlanningProjectStatus;
  /** Who opened it. `customer` cards are the ones the portal produced. */
  origin: PlanningProjectOrigin;
  /** On a `customer` card: the portal's form or the customer's own system (the customer API). */
  submittedVia: PlanningSubmissionChannel;
  /** The customer's own id for a request filed through the customer API. Null otherwise. */
  externalReference: string | null;
  typeId: number;
  typeName: string;
  typeCode: PlanningProjectTypeCode | null;
  categoryId: number | null;
  categoryName: string | null;
  /** By name. Empty, never null, on a project with none. */
  tags: PlanningProjectTagDto[];
  owner: PlanningUserRef | null;
  waitingOn: PlanningWaitingOn;
  productionCustomerId: number | null;
  productionCustomerName: string | null;
  /**
   * סוג לקוח, from the organisation's own card. Null on a project whose customer never had one set.
   *
   * On the row because the card branches on it: the customer-quote area opens for an external or a
   * partnership customer and stays shut for internal work (`projectTakesCustomerQuote`).
   */
  productionCustomerType: ProductionCustomerType | null;
  /** Open tasks — the `todo` and `doing` columns together. `done` never counts. */
  openTaskCount: number;
  lateTaskCount: number;
  dueDate: string | null;
  targetUnitCost: number | null;
  currency: PhCurrency;
  /** The supplier chosen for the most components. Null until one is. */
  leadSupplierName: string | null;
  /** `updatedAt`, or the last message — whichever is later. The mockup's "עדכון אחרון". */
  lastActivityAt: string;
}

/** The full card. Sections come from their own endpoints; this is the header and the summary. */
export interface PlanningProjectDto extends PlanningProjectRow, PlanningSpec {
  description: string | null;
  /** "הערות כלליות ובקשות נוספות" from the portal's order form. The customer's own words, untouched. */
  customerNotes: string | null;
  /** Who filed the request from the portal, and when. Null on anything opened here. */
  submittedBy: PlanningUserRef | null;
  submittedAt: string | null;
  plannedQuantity: number | null;
  linkedProduct: PlanningLinkedProduct | null;
  /** The project this one was opened as a new round of. Null for `new` / `other`. */
  sourceProject: PlanningProjectSourceRef | null;
  imageUrl: string | null;
  itemCount: number;
  readyAt: string | null;
  archivedAt: string | null;
  createdBy: PlanningUserRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertPlanningProjectBody extends Partial<PlanningSpec> {
  name: string;
  typeId: number;
  categoryId?: number | null;
  /** The whole set — a PATCH that carries it replaces the project's tags; one that omits it keeps them. */
  tagIds?: number[];
  /**
   * Nullable since the portal exists: a request that just landed has nobody on it yet, and
   * "טרם שויך" is a real state rather than a default somebody has to invent. A project opened
   * from the team's own screen still sends one — the drawer requires it.
   */
  ownerUserId?: number | null;
  /**
   * The production customer the work is for — **required on create**, for every type.
   *
   * Not required on a PATCH, and not clearable once set: the rows that predate the rule would
   * otherwise become unsaveable, which is a rule enforced by refusing to let anyone fix anything.
   * Same shape as the phone rule on `ProductionCustomers`.
   */
  productionCustomerId: number;
  dueDate?: string | null;
  description?: string | null;
  sku?: string | null;
  plannedQuantity?: number | null;
  targetUnitCost?: number | null;
  currency?: PhCurrency;
  /**
   * An optional pointer at the catalogue row this project became, for the card's "מוצר במערכת"
   * link. It is **not** what an upgrade or a new round is opened from — that is `sourceProjectId`,
   * a development project (`requiresSourceProject`).
   */
  productId?: number | null;
  /**
   * The earlier development project to open this one as a new round of — required when the type's
   * code is `upgrade` or `production_round`.
   *
   * On **create** it also seeds the new card: the source's name, components, suppliers, prices and
   * files are copied over (`PLANNING_CLONED_SECTIONS`), and its tasks and chat are not. On a PATCH
   * it only re-points the link — nothing is copied a second time.
   */
  sourceProjectId?: number | null;
}

/** The header dropdown writes one of these two, never both — see the note in `productPlanning.core.ts`. */
export interface ChangePlanningStageBody {
  stage?: PlanningStage;
  status?: PlanningProjectStatus;
  /** The mockup's "אחראי על השלב" — reassigns the project when set. */
  ownerUserId?: number | null;
  dueDate?: string | null;
  /** Goes into the system message the change writes. */
  note?: string | null;
}

/* ── kit items ────────────────────────────────────────────────────────────────────────────── */

/** One component of the kit. "חנוכייה", "נרות", "קופסת מתנה". */
export interface PlanningProjectItemDto extends PlanningSpec {
  id: number;
  projectId: number;
  name: string;
  sku: string | null;
  /** "ברקוד" — pre-fills the line's barcode when the component is ordered. */
  barcode: string | null;
  /** Free text, because the mockup shows both "x1" and "x1 חבילה". */
  quantityLabel: string | null;
  /** "תיאור" — what the component is. Typed by the team in the items drawer. */
  description: string | null;
  /** "הערות" — a type, a material, a special request. The portal's order form writes it too. */
  notes: string | null;
  /** "כמות יחידות ליצור" — how many of this component to make in total. */
  quantityToProduce: number | null;
  /** "כמות יחידות בכל ערכה" — optional, and only meaningful when the project is a kit. */
  quantityPerKit: number | null;
  targetUnitCost: number | null;
  currency: PhCurrency;
  productVariantId: number | null;
  /** The chosen quote. Null means "טרם נבחר ספק", which the card draws in red. */
  selectedQuoteId: number | null;
  selectedQuote: PlanningQuoteDto | null;
  quoteCount: number;
  sortOrder: number;
}

export interface UpsertPlanningProjectItemBody extends Partial<PlanningSpec> {
  name: string;
  sku?: string | null;
  barcode?: string | null;
  quantityLabel?: string | null;
  description?: string | null;
  notes?: string | null;
  /** "כמות יחידות ליצור" — what the card's item rows type, and what a production order draws on. */
  quantityToProduce?: number | null;
  quantityPerKit?: number | null;
  targetUnitCost?: number | null;
  currency?: PhCurrency;
  productVariantId?: number | null;
  sortOrder?: number;
}

/* ── quotes ───────────────────────────────────────────────────────────────────────────────── */

/** A price for a quantity. The mockup's "מדרגות כמות". */
export interface PlanningQuoteTierDto {
  id: number;
  quantity: number;
  unitPrice: number;
}

export interface PlanningQuoteDto {
  id: number;
  projectId: number;
  /** Null means the quote covers the whole kit rather than one component ("המארז כולו"). */
  itemId: number | null;
  itemName: string | null;
  supplier: PlanningSupplierRef;
  unitPrice: number | null;
  currency: PhCurrency;
  moq: number | null;
  leadTimeDays: number | null;
  toolingCost: number | null;
  validUntil: string | null;
  notes: string | null;
  /** The quote PDF, when one was attached. */
  documentAssetId: number | null;
  documentFileName: string | null;
  documentUrl: string | null;
  tiers: PlanningQuoteTierDto[];
  /** ממתין / הצעה חדשה מהספק / מאושר / נדחה / בוטל. `approved` is the decision the rest of the module reads. */
  status: PlanningQuoteStatus;
  /**
   * True when this row was written by the supplier through the public link, not typed on the card.
   *
   * Kept beside `status`: approving the offer changes the status and must not erase where it came
   * from. The card prints "התקבלה דרך קישור" from this flag.
   */
  viaSupplierLink: boolean;
  /** True when this is the quote its item selected. Computed, never stored on the quote. */
  isSelected: boolean;
  /**
   * Whether **this** is the offer a production order would be placed at.
   *
   * Not simply `status === 'approved'`, and that is the point: a component with a single quote on
   * it needs no ceremony — the requester was explicit that one offer goes straight through to the
   * work order without anybody pressing approve. So it is the approved quote of its scope (its
   * component, or the kit for an `itemId: null` offer), or the only quote in that scope when
   * nothing has been approved there.
   *
   * Computed per scope on every read, like `isSelected` and for the same reason: stored, it would
   * have to be recomputed by every write that adds or removes a sibling quote.
   */
  isForOrder: boolean;
  createdBy: PlanningUserRef | null;
  createdAt: string;
}

export interface UpsertPlanningQuoteBody {
  itemId?: number | null;
  supplierId: number;
  unitPrice: number;
  currency?: PhCurrency;
  moq?: number | null;
  leadTimeDays?: number | null;
  toolingCost?: number | null;
  validUntil?: string | null;
  notes?: string | null;
  documentAssetId?: number | null;
  tiers?: { quantity: number; unitPrice: number }[];
}

/**
 * `POST /planning/quote-documents` — the supplier's quote file, uploaded before the quote is saved.
 *
 * The id goes back in `UpsertPlanningQuoteBody.documentAssetId`; the name and URL let the drawer
 * show what was attached without waiting for the save.
 */
export interface PlanningQuoteDocumentDto {
  documentAssetId: number;
  fileName: string;
  url: string;
}

/* ── the cost summary the card shows above the components ─────────────────────────────────── */

/**
 * What the four tiles under "ספקים והצעות מחיר" say.
 *
 * `isPartial` is the whole point of the shape: with one component still unpriced the total is a
 * range and not a number, and the mockup says so in an orange banner rather than showing a total
 * that reads as final. Computed on the server so the screen cannot decide differently.
 */
export interface PlanningCostSummary {
  currency: PhCurrency;
  /** Components with a chosen supplier, out of all of them. */
  pricedItemCount: number;
  itemCount: number;
  /** The sum of the chosen quotes. Complete only when `isPartial` is false. */
  approvedUnitCost: number;
  /** Cheapest and dearest the kit could still land at, given the quotes on the table. */
  estimatedUnitCostMin: number;
  estimatedUnitCostMax: number;
  totalForPlannedQuantityMin: number | null;
  totalForPlannedQuantityMax: number | null;
  /** The longest lead time among the chosen quotes, in days. */
  longestLeadTimeDays: number | null;
  targetUnitCost: number | null;
  isPartial: boolean;
  /** Names of the components still without a supplier, for the banner. */
  unpricedItemNames: string[];
}

/* ── files ────────────────────────────────────────────────────────────────────────────────── */

export interface PlanningFileVersionDto {
  id: number;
  revision: number;
  isCurrent: boolean;
  notes: string | null;
  /** The stored file — what a supplier quote names when it is picked from the folder. */
  documentAssetId: number;
  fileName: string;
  fileUrl: string;
  fileSize: number | null;
  mimeType: string | null;
  uploadedBy: PlanningUserRef | null;
  createdAt: string;
}

/**
 * A file as the team thinks of it: one name, many revisions.
 *
 * "Artwork REV 5" is not a new file, it is the fifth version of one, and the mockup shows the
 * chain with REV 4 marked Current. Marking a new revision current does **not** delete the others —
 * the drawer says so in as many words ("הן לא נמחקות").
 */
export interface PlanningFileGroupDto {
  id: number;
  projectId: number;
  itemId: number | null;
  itemName: string | null;
  name: string;
  category: PlanningFileCategory;
  /** Shared with the customer. `false` unless somebody said otherwise — see the portal's file rule. */
  isCustomerVisible: boolean;
  versions: PlanningFileVersionDto[];
  currentVersion: PlanningFileVersionDto | null;
  updatedAt: string;
}

/** The multipart fields that ride along with the uploaded file. */
export interface UploadPlanningFileBody {
  name: string;
  category: PlanningFileCategory;
  itemId?: number | null;
  /** Adds a revision to an existing group instead of starting a new one. */
  groupId?: number | null;
  /** The drawer's "קובץ סופי" checkbox. Unchecked by default — a fresh upload is not a decision. */
  isCurrent?: boolean;
  notes?: string | null;
}

/**
 * Editing a file after it was uploaded — the pencil on the file card.
 *
 * Metadata only: the bytes of a revision are never replaced in place, because a REV that changed
 * content under a name somebody already approved is exactly what the revision chain exists to
 * prevent. A new version of the content is a new upload.
 *
 * `notes` is a property of a revision and not of the group, so it is written onto the newest one —
 * the revision the card is showing when the pencil is pressed.
 */
export interface UpdatePlanningFileGroupBody {
  name?: string;
  category?: PlanningFileCategory;
  itemId?: number | null;
  notes?: string | null;
  /** The file card's "שיתוף עם הלקוח" toggle. */
  isCustomerVisible?: boolean;
}

/* ── sample review ────────────────────────────────────────────────────────────────────────── */

export interface PlanningSampleReviewDto {
  id: number;
  projectId: number;
  itemId: number | null;
  itemName: string | null;
  revisionLabel: string;
  supplier: PlanningSupplierRef | null;
  receivedAt: string | null;
  reviewer: PlanningUserRef | null;
  decision: PlanningSampleDecision;
  notes: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface CreatePlanningSampleReviewBody {
  itemId?: number | null;
  revisionLabel: string;
  supplierId?: number | null;
  receivedAt?: string | null;
  decision: PlanningSampleDecision;
  /** Required unless the decision is `approve` — see `sampleNotesRequired`. */
  notes?: string | null;
}

/* ── the ready-for-production gate ────────────────────────────────────────────────────────── */

export const PLANNING_GATE_CHECKS = [
  'spec_approved',
  'supplier_chosen',
  'quote_approved',
  'quote_chosen',
  'quantity_set',
  'sample_approved',
  'no_blocking_tasks'
] as const;
export type PlanningGateCheckKey = (typeof PLANNING_GATE_CHECKS)[number];

export const PLANNING_GATE_CHECK_LABELS: Record<PlanningGateCheckKey, string> = {
  spec_approved: 'מפרט מאושר',
  supplier_chosen: 'ספק נבחר',
  quote_approved: 'הצעת מחיר מאושרת',
  quote_chosen: 'הצעת מחיר נבחרה',
  quantity_set: 'כמות ייצור מוגדרת',
  sample_approved: 'דוגמה מאושרת',
  no_blocking_tasks: 'אין משימות חוסמות'
};

export interface PlanningGateCheck {
  key: PlanningGateCheckKey;
  ok: boolean;
  /** The short right-hand explanation: "REV 4", "0 חסמים", "משימת אישור פתוחה". */
  note: string;
}

/**
 * Computed on every read, never stored, and **not a gate**.
 *
 * A manual change to `status: 'ready'` always succeeds. This checklist is what
 * `GET /planning/projects/:id/readiness` and the production-order draft still report — a missing
 * sample or an open task is information, not a refusal. `readyAt` is stamped when the status is
 * set to ready, not when these checks pass.
 */
export interface PlanningReadinessDto {
  checks: PlanningGateCheck[];
  isReady: boolean;
  readyAt: string | null;
}

/* ── the price we offer the customer ──────────────────────────────────────────────────────── */

/**
 * A price quoted **to** the production customer — the other direction of trade from
 * `PlanningQuoteDto`, which is a supplier quoting a price **to us**.
 *
 * They are two tables and two DTOs on purpose. A supplier quote has a supplier, an MOQ, a lead time
 * and tooling, and it is our cost; this has a validity date, a status, and it is our price. Folding
 * them into one row with a nullable `supplierId` would produce a table where "what does this kit
 * cost" and "what did we charge for it" are the same query with a flag, and margin would be one
 * forgotten `WHERE` away from the portal.
 *
 * A quote carries **a priced line per component chosen for it** (`PlanningCustomerQuoteLineDto`),
 * picked from the components that already have a supplier offer. Not every component of the
 * project: a project quoted in parts would otherwise repeat every component on every quote. What it
 * never mirrors is the supplier's price — that is the number this table exists to replace.
 */
export const PLANNING_CUSTOMER_QUOTE_STATUSES = [
  'new',
  'in_progress',
  'pending_approval',
  'approved',
  'on_hold',
  'cancelled'
] as const;
export type PlanningCustomerQuoteStatus = (typeof PLANNING_CUSTOMER_QUOTE_STATUSES)[number];

export const PLANNING_CUSTOMER_QUOTE_STATUS_LABELS: Record<PlanningCustomerQuoteStatus, string> = {
  new: 'חדשה',
  in_progress: 'בטיפול',
  pending_approval: 'ממתין לאישור',
  approved: 'מאושר',
  on_hold: 'מושהה',
  cancelled: 'בוטל'
};
export const PLANNING_CUSTOMER_QUOTE_STATUS_OPTIONS = phOptions(
  PLANNING_CUSTOMER_QUOTE_STATUSES,
  PLANNING_CUSTOMER_QUOTE_STATUS_LABELS
);

/** Where a quote starts: nobody has priced anything yet. */
export const PLANNING_CUSTOMER_QUOTE_INITIAL: PlanningCustomerQuoteStatus = 'new';

/**
 * The two statuses a quote's prices may still be edited in.
 *
 * **Not a visibility rule any more.** The customer sees every quote from the moment it is saved
 * (feedback round, 2026-10-12): a manager filled one in, left it `חדשה`, and the customer kept
 * reading "טרם נשלחה הצעת מחיר". The status is now only where the quote stands — and the edit lock
 * still stops at `ממתין לאישור`, so a price somebody already approved cannot move under them.
 */
export const PLANNING_CUSTOMER_QUOTE_EDITABLE_STATUSES: readonly PlanningCustomerQuoteStatus[] = ['new', 'in_progress'];

/**
 * The customer types whose card opens the area — asked for by name: לקוח חיצוני קבוע, לקוח חיצוני
 * מזדמן, לקוח שותפות.
 *
 * `internal` is left out on purpose: work we do for ourselves is priced in the supplier area above
 * and there is nobody to quote it to. A project whose customer type is still `null` (rows that
 * predate the field) shows nothing either — "nobody has said yet" is not a licence to quote.
 */
export const PLANNING_CUSTOMER_QUOTE_CUSTOMER_TYPES: readonly ProductionCustomerType[] = [
  'external_regular',
  'external_occasional',
  'partnership'
];

export const projectTakesCustomerQuote = (customerType: ProductionCustomerType | null): boolean =>
  customerType !== null && PLANNING_CUSTOMER_QUOTE_CUSTOMER_TYPES.includes(customerType);

/**
 * One priced line of a customer quote — a component of the kit, and what we charge for it.
 *
 * Everything but the price is **read from the project's components live** and never copied into the
 * quote: the area "מושך מאזור ספקים והצעות מחיר את כל הפרטים חוץ מהמחיר", so a component renamed in
 * the supplier area above is renamed here in the same breath. The only thing the quote itself owns
 * is `unitPrice` — the one number the supplier area cannot answer, because it is ours and not
 * theirs.
 *
 * Only the components chosen for the quote arrive — a line exists because somebody priced it.
 */
export interface PlanningCustomerQuoteLineDto {
  /** `null` — the product itself ("המוצר כולו"), as `PlanningQuoteDto.itemId` is on the supplier side. */
  projectItemId: number | null;
  name: string;
  sku: string | null;
  quantityLabel: string | null;
  quantityToProduce: number | null;
  quantityPerKit: number | null;
  notes: string | null;
  /** The supplier chosen for the component, for the reader's orientation. Never their price. */
  supplierName: string | null;
  /** What `totalPrice` multiplies by: the component's own quantity, or the project's planned one. */
  quantity: number | null;
  unitPrice: number | null;
  /** `unitPrice × quantity`, derived — the field the requester asked to fill itself. */
  totalPrice: number | null;
}

export interface PlanningCustomerQuoteDto {
  id: number;
  projectId: number;
  quantity: number | null;
  /**
   * The single price of a quote written before the area had lines. Null on everything since.
   *
   * Kept so the old rows still render a number instead of a dash; nothing writes it any more.
   */
  unitPrice: number | null;
  currency: PhCurrency;
  /** Stored and not derived: a quoted total may be rounded, discounted, or simply agreed. */
  totalPrice: number | null;
  /** A row per component chosen for this quote, in the order the supplier area draws them. */
  lines: PlanningCustomerQuoteLineDto[];
  /** "סה״כ עלות למוצר / לכל הערכה (לפני מע״מ ומשלוחים)" — the sum of the priced lines. */
  linesTotal: number | null;
  /** "עלות משלוח משוערת" — optional, and deliberately outside `linesTotal`. */
  shippingEstimate: number | null;
  /** The shipping estimate's own currency, apart from the lines' `currency`. */
  shippingCurrency: PhCurrency;
  validUntil: string | null;
  notes: string | null;
  status: PlanningCustomerQuoteStatus;
  /** The quote document, when one was attached. The portal serves it through a signed download. */
  documentAssetId: number | null;
  documentFileName: string | null;
  sentAt: string | null;
  decidedAt: string | null;
  decidedBy: PlanningUserRef | null;
  createdBy: PlanningUserRef | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * "מע״מ" — added on top of the lines and the shipping for "סה״כ מחיר כולל מע״מ ומשלוח".
 *
 * Applied on screen only and never stored: the quote keeps its pre-VAT numbers, and a rate change is
 * one edit here rather than a migration of every quote.
 */
export const PLANNING_VAT_RATE = 0.18;

/** The pre-VAT amounts plus VAT. `null` when there is nothing to add it to. */
export const withVat = (...amounts: (number | null)[]): number | null => {
  const present = amounts.filter((amount): amount is number => amount !== null);
  return present.length ? present.reduce((sum, amount) => sum + amount, 0) * (1 + PLANNING_VAT_RATE) : null;
};

/** One line's price, as the drawer sends it. `null` clears a price somebody typed by mistake. */
export interface PlanningCustomerQuoteLineBody {
  /** `null` prices the product itself ("המוצר כולו"). */
  projectItemId: number | null;
  unitPrice: number | null;
}

export interface UpsertPlanningCustomerQuoteBody {
  quantity?: number | null;
  currency?: PhCurrency;
  /**
   * The priced lines, **as a whole set**: what arrives replaces what is stored, and a component
   * left out of the array leaves the quote. Sending `[]` clears every line on the quote.
   */
  lines?: PlanningCustomerQuoteLineBody[];
  shippingEstimate?: number | null;
  shippingCurrency?: PhCurrency;
  totalPrice?: number | null;
  validUntil?: string | null;
  notes?: string | null;
  /** Free to move through `PLANNING_CUSTOMER_QUOTE_STATUSES` — see the note on the internal two. */
  status?: PlanningCustomerQuoteStatus;
}
